import axios from "axios";
import { base_url, config, throttledRequest } from "../../utils/axiosConfig";

// Add retry logic for rate limited requests
const axiosRetry = async (fn, retries = 3, delay = 1000) => {
  try {
    return await fn();
  } catch (error) {
    if (error.response?.status === 429 && retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return axiosRetry(fn, retries - 1, delay * 2);
    }
    throw error;
  }
};

const register = async (userData) => {
  const response = await axios.post(`${base_url}user/register`, userData);
  if (response.data) {
    return response.data;
  }
};

const login = async (userData) => {
  const response = await axiosRetry(() => 
    axios.post(`${base_url}user/login`, userData)
  );

  if (response.data) {
    localStorage.setItem("customer", JSON.stringify(response.data));
  }
  return response.data;
};

const getUserWislist = async () => {
  const response = await axios.get(`${base_url}user/wishlist`, config);
  if (response.data) {
    return response.data;
  }
};

const getUserCoupons = async (configData) => {
  const response = await axios.get(`${base_url}coupon/user-coupons`, configData || config);
  if (response.data) {
    return response.data.data || response.data;
  }
};

const getUserReviews = async (configData) => {
  const response = await axios.get(`${base_url}product/user-ratings`, configData || config);
  if (response.data) {
    return response.data.data || response.data;
  }
};

const getUserNotifications = async (configData) => {
  const response = await axios.get(`${base_url}user/notifications`, configData || config);
  if (response.data) {
    return response.data.data || response.data;
  }
};

const addToCart = async (cartData) => {
  const response = await throttledRequest(() => 
    axios.post(`${base_url}user/cart`, cartData, config)
  );
  if (response.data) {
    return response.data;
  }
};

const getCart = async (data) => {
  const response = await throttledRequest(() => 
    axiosRetry(() => axios.get(`${base_url}user/cart`, data || config))
  );
  if (response.data) {
    return response.data;
  }
};

const removeProductFromCart = async (data) => {
  const response = await axios.delete(
    `${base_url}user/delete-product-cart/${data.id}`,

    data.config2
  );
  if (response.data) {
    return response.data;
  }
};

const updateProductFromCart = async (cartDetail) => {
  const response = await axios.put(
    `${base_url}user/update-product-cart/${cartDetail.cartItemId}`,
    cartDetail,
    config
  );
  if (response.data) {
    return response.data;
  }
};

const createOrder = async (orderDetail) => {
  const response = await axios.post(
    `${base_url}user/cart/create-order/`,
    orderDetail,
    config
  );
  if (response.data) {
    return response.data;
  }
};

const getUserOrders = async (configData) => {
  const response = await axios.get(`${base_url}user/getmyorders`, configData || config);

  if (response.data) {
    return response.data;
  }
};

const updateUser = async (data) => {
  const response = await axios.put(
    `${base_url}user/edit-user`,
    data.data,
    data.config2,
    config
  );

  if (response.data) {
    return response.data;
  }
};

const forgotPasswordToken = async (data) => {
  const response = await axios.post(
    `${base_url}user/forgot-password-token`,
    data
  );

  if (response.data) {
    return response.data;
  }
};

const resetPass = async (data) => {
  const response = await axios.put(
    `${base_url}user/reset-password/${data.token}`,
    {
      password: data?.password,
    }
  );

  if (response.data) {
    return response.data;
  }
};

const mergeGuestCart = async (items) => {
  // items: array of guest cart items — add each to server cart sequentially
  for (const item of items) {
    try {
      await axios.post(`${base_url}user/cart`, {
        productId: item.productId._id,
        quantity: item.quantity,
        price: item.price,
        color: item.color?._id || null,
        size: item.size?._id || null,
        customSize: item.customSize || null,
      }, config);
    } catch (e) {
      // skip items that fail (e.g. product no longer exists)
    }
  }
};

const emptyCart = async (data) => {
  const response = await axios.delete(`${base_url}user/empty-cart`, data);

  if (response.data) {
    return response.data;
  }
};

const getUserAddresses = async (configData) => {
  const response = await axios.get(`${base_url}address`, configData || config);
  if (response.data) {
    return response.data.data || response.data; // Handle both old and new format
  }
};

const createAddress = async (addressData) => {
  const response = await axios.post(`${base_url}address`, addressData.addressData, addressData.config2);
  if (response.data) {
    return response.data.data || response.data;
  }
};

const deleteAddress = async (data) => {
  const response = await axios.delete(`${base_url}address/${data.id}`, data.config2);
  if (response.data) {
    return response.data;
  }
};

export const authService = {
  register,
  login,
  getUserWislist,
  getUserCoupons,
  getUserReviews,
  getUserNotifications,
  addToCart,
  getCart,
  removeProductFromCart,
  updateProductFromCart,
  createOrder,
  getUserOrders,
  updateUser,
  forgotPasswordToken,
  resetPass,
  mergeGuestCart,
  emptyCart,
  getUserAddresses,
  createAddress,
  deleteAddress,
};
